<template>
  <div v-if="event">
    <CustomHead :head="head" />
    <PageHeader background="#FFFFFF" color="primary">
      <div class="event-header mks-grid mks-grid-gap-xl">
        <mks-spacer class="event-header__metas mks-col-12 mks-col-6@sm">
          <EventCardTags :event="event" />
          <Date
            :event="event"
            big
            showLocation
            showDuration
            showTimesForMultiDayEvents
          ></Date>
          <mks-spacer spacing="s">
            <mks-heading
              v-if="event.title"
              tag="h1"
              tag-class="h1"
              class="event__title"
              >{{ event.title[event.lang] }}</mks-heading
            >
          </mks-spacer>
          <EventCardParticipateButton
            :event="event"
            :showParticipateButton="true"
            :showIfEventIsPast="true"
            source="single-event"
          />
        </mks-spacer>
        <div class="event-header__image mks-col-12 mks-col-6@sm mks-col-end">
          <EventCardCover class="event__cover-wrapper" :event="event">
            <Like
              v-if="!userLoading && isLoggedIn"
              :id="event._id"
              class="cover__like"
              @liked="$emit('liked', event._id)"
              @removed="$emit('removed', event._id)"
            >
            </Like>
          </EventCardCover>
        </div>
      </div>
    </PageHeader>
    <main class="page-event__content">
      <div class="event__content mks-grid">
        <mks-spacer spacing="xl" class="mks-col-8@sm">
          <mks-heading tag="h2" tag-class="h3">{{
            __("Description de l'événement")
          }}</mks-heading>
          <div
            v-if="event.description"
            class="event__description rte-render"
            v-html="event.description[event.lang]"
          ></div>
          <mks-spacer spacing="s" v-if="event.external">
            <mks-text size="small" tag="p" color="light"
              >{{
                __(
                  "Cet événement est organisé par un membre de la communauté en total indépendance de l'association makesense. Les organisateurs se sont engagés à suivre les conditions générales d'utilisation de la plateforme."
                )
              }}
            </mks-text>
          </mks-spacer>
          <!--AddToCalendar :id="event._id" /-->
        </mks-spacer>
        <EventSidebar
          :event="event"
          class="event__sidebar mks-col-4@sm"
          @contact="onContact($event)"
        ></EventSidebar>

        <mks-spacer class="map__wrapper" v-if="event.address">
          <Map
            :googleKey="$env.google.apiKey"
            :address="event.address[event.lang].formattedAddress"
          ></Map>
          <mks-spacer class="map__address" inline align="center" distribute="center">
            <div class="spacer__item" v-if="event.address">
              <mks-text tag="p" weight="semibold" align="center" class="">{{
                __("Cet événement se déroulera :")
              }}</mks-text>
              <mks-text
                v-if="!event.source"
                tag="p"
                weight="bold"
                size="large"
                align="center"
              >
                {{
                  event.address[event.lang] && event.address[event.lang].formattedAddress
                }}</mks-text
              >
            </div>

            <mks-button size="round" type="neutral" @click="copyAddress"
              ><mks-icon type="copy"></mks-icon
            ></mks-button>
          </mks-spacer>
        </mks-spacer>
        <RelatedEvents
          v-if="
            $env.SITE === 'international' && relatedEvents && relatedEvents.length > 0
          "
          :events="relatedEvents"
        />
        <mks-text tag="div" align="center" v-if="$env.SITE === 'international'">
          <mks-link :to="localePath(`/`)" class="link" type="menu">
            <mks-button type="secondary" size="small">{{
              __("Retour à l'accueil")
            }}</mks-button>
          </mks-link>
        </mks-text>
      </div>
    </main>

    <Section
      v-if="$env.SITE === 'chiche' && relatedEvents && relatedEvents.length > 0"
      radius="top"
    >
      <mks-spacer spacing="xl">
        <Heading
          tag="h2"
          textBefore="D'autres "
          textBean="rencontres"
          textAfter=" stylées"
          colorBean="parsley"
        ></Heading>
        <Carousel :showNavigation="true" navColor="primary" :slides-per-view="3">
          <EventTeaser
            v-for="item in relatedEvents"
            :key="item._id"
            :event="item"
            variant="flat"
            :show-description="false"
          ></EventTeaser>
        </Carousel>
      </mks-spacer>
    </Section>

    <Section
      v-if="$env.SITE === 'chiche' && relatedArticles && relatedArticles.length > 0"
    >
      <mks-spacer spacing="xl">
        <Heading
          tag="h2"
          textBefore="On se "
          textBean="cultive"
          textAfter=""
          colorBean="parsley"
        ></Heading>
        <Carousel :showNavigation="true" navColor="primary" :slides-per-view="3">
          <ArticleTeaser
            v-for="item in relatedArticles"
            :key="item._id"
            :content="item"
            variant="spotlight"
            :show-description="false"
          ></ArticleTeaser>
        </Carousel>
      </mks-spacer>
    </Section>

    <NewsletterHero v-if="lang === 'fr'" source="event-single-page" />
    <ModalContactOrganizer
      v-if="showContactModal"
      :show="showContactModal"
      :user="organizer"
      :event="event._id"
      @close="onCloseContactModal"
    />
    <template v-if="$route.name !== localePath({ name: 'e-slug' })">
      <MksLoading v-if="userLoading" />
      <NuxtPage
        :event="event"
        :related="relatedEvents"
        :articles="relatedArticles"
        v-else
        @unattend="unattended"
      ></NuxtPage>
    </template>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import dateMixin from "@/mixins/date";
import eventMetasMixin from "@/mixins/eventMetas";

import AddToCalendar from "@/components/organisms/AddToCalendar.vue";
import EventSidebar from "@/components/organisms/EventSidebar.vue";
import Map from "@/components/organisms/Map.vue";
import MksBubble from "@DS/components/Bubble.vue";
import MksHero from "@DS/components/Hero.vue";
import ModalContactOrganizer from "@/components/modals/ModalContactOrganizer.vue";
import NewsletterHero from "@/components/organisms/NewsletterHero.vue";
import RelatedEvents from "@/components/organisms/RelatedEvents.vue";
import Share from "@/components/organisms/Share.vue";
import { useRoute } from "vue-router";

export default defineNuxtComponent({
  components: {
    AddToCalendar,
    EventSidebar,
    Map,
    ModalContactOrganizer,
    MksBubble,
    MksHero,
    NewsletterHero,
    RelatedEvents,
    Share
  },
  mixins: [dateMixin, eventMetasMixin],
  async asyncData(NuxtApp) {
    const nuxtApp = useNuxtApp();
    const route = useRoute();
    try {
      const slug = route.params.slug;
      const event = await NuxtApp.$api.Events.bySlug(slug);

      const relatedCriterias = {
        limit: 5,
        only: "articles"
      };
      if (event.topic) relatedCriterias.topics = [event.topic._id];

      // if event.endDate before 12 months ago, send to error 'GONE' page
      // const aYearAgo = nuxtApp.$dayjs();
      const aYearAgo = NuxtApp.$dayjs().subtract("1", "year");
      if (NuxtApp.$dayjs(event.endDate).isBefore(aYearAgo)) {
        throw createError({ statusCode: 410, statusMessage: "Old event" });
      }

      let related;
      try {
        related = await NuxtApp.$api.Builder.queryContent(relatedCriterias);
      } catch (err) {
        console.log("error fetching related events", err);
      }

      return { slug, event, relatedArticles: related };
    } catch (e) {
      if (e.statusCode === 410) {
        throw createError({ statusCode: 410, statusMessage: "Old event", fatal: true });
      } else error({ statusCode: 404, message: "Post not found" });
      return {
        event: null,
        slug: params.slug
      };
    }
  },
  data() {
    return {
      // event: {},
      relatedEvents: null,
      showContactModal: false,
      organizer: null,
      displayAd: null,
      scroll: null
    };
  },
  computed: {
    ...mapGetters("user", ["userLoading"]),
    pastEvent() {
      return this.$dayjs(this.event.endDate).isBefore(this.$dayjs());
    },
    popupCondition() {
      return (
        this.event &&
        this.event.topic &&
        this.event.topic.slug &&
        this.event.topic.slug.fr !== "entrepreneur" &&
        this.event.topic.slug.fr !== "education" &&
        this.event.topic.slug.fr !== "transition-des-organisations" &&
        this.event.topic.slug.fr !== "communaute" &&
        this.event.topic.slug.fr !== "quetesens" &&
        this.lang === "fr"
      );
    },
    sanitizedDescription() {
      return this.event.description && this.event.description[this.event.lang]
        ? this.event.description[this.event.lang].replace(/<[^>]+>/g, " ")
        : "";
    }
  },
  created() {
    if (process.client) window.addEventListener("scroll", this.handleScroll);
  },
  destroyed() {
    if (process.client) window.removeEventListener("scroll", this.handleScroll);
  },
  async mounted() {
    if (process.client) {
      // d&sactivé car ça me balance une erreur 'unauthorized'
      // const res = await this.$api.Like.getLikes([this.event._id])
      // this.liked = res[this.event._id];
    }
    this.getRelated();
    this.$store.dispatch("analytics/track", {
      event: "Product Viewed",
      data: {
        event_id: this.event._id,
        product_id: this.event._id,
        event_title: this.event.title && this.event.title[this.event.lang],
        event_date: this.event.date,
        brand: "makesense",
        quantity: this.event.maxAttendees,
        email: this.$store.getters["user/getEmail"],
        url: this.event.slug,
        category:
          this.event.topic &&
          this.event.topic.name &&
          this.event.topic.name[this.event.lang],
        name: this.event.title && this.event.title[this.event.lang],
        event_type:
          this.event.type &&
          this.event.type.name &&
          this.event.type.name[this.event.lang],
        event_topic:
          this.event.topic &&
          this.event.topic.name &&
          this.event.topic.name[this.event.lang],
        event_momentum:
          this.event.momentum &&
          this.event.momentum.name &&
          this.event.momentum.name[this.event.lang],
        image_url:
          this.event.cover &&
          this.event.cover[this.event.lang] &&
          this.event.cover[this.event.lang].url
      }
    });
  },
  watch: {
    event() {
      if (this.event) {
        this.getRelated();
      }
    },
    scroll(value) {
      if (this.displayAd === null && value > 500) this.onDisplayAd();
    }
  },
  methods: {
    copyAddress() {
      this.$copyText(this.event.address[this.lang].formattedAddress).then(
        (e) => {
          this.$toasted.show(this.__("C'est copié, manque plus qu'à coller !"), {
            type: "info",
            position: "bottom-center",
            duration: 2000
          });
        },
        (e) => {
          this.$toasted.show(this.__("Oups, une erreur s'est produite :("), {
            type: "error",
            position: "bottom-center",
            duration: 2000
          });
        }
      );
    },
    async getRelated() {
      const relatedEvents = await this.$api.Events.related(this.event._id);
      this.relatedEvents = relatedEvents
        .sort(function (a, b) {
          // Turn your strings into dates, and then subtract them
          // to get a value that is either negative, positive, or zero.
          return new Date(a.date) - new Date(b.date);
        })
        .splice(0, 3);
    },
    onContact(user) {
      this.organizer = user._id;
      this.showContactModal = true;
      this.$store.dispatch("analytics/track", {
        event: "Front - Open organizator messaging",
        data: {
          label: "Message",
          category: "Event",
          email: this.$store.state.user.email,
          event_id: this.event._id
        }
      });
    },
    onCloseContactModal() {
      this.showContactModal = false;
      this.organizer = null;
    },
    handleScroll() {
      this.scroll = window.scrollY;
    },
    onCloseAd() {
      this.displayAd = false;
      this.$store.dispatch("analytics/track", {
        event: "Event Single - Closed Bubble Ad",
        data: {
          event: this.event._id
        }
      });
    },
    onClickAd() {
      this.$store.dispatch("analytics/track", {
        event: "Event Single - Clicked Bubble Ad",
        data: {
          event: this.event._id,
          version: "2"
        }
      });
    },
    onDisplayAd() {
      this.displayAd = true;
      this.$store.dispatch("analytics/track", {
        event: "Event Single - Displayed Bubble Ad",
        data: {
          event: this.event._id
        }
      });
    },
    unattended() {
      this.refresh();
    },
    async refresh() {
      try {
        this.event = await this.$api.Events.bySlug(this.$route.params.slug);
      } catch (e) {
        console.log("error while refreshing", e);
      }
    }
  }
});
</script>

<style lang="scss" scoped>
.event-header {
  color: $color-text;
}

.event__title {
  @include breakpoint("small", "max") {
    font-size: $font-size-xxxl;
  }
}

.page-event__content {
  background: $color-neutral-95;
}
.event__hero {
  @include inner-space("xxxl", "b");
  position: relative;
}
.event__participants {
  position: absolute;
  top: 0;
  right: $space-s;
  transform: translateY(-100%);
}
.event__content {
  // background: $color-neutral-95;
  @include container-spacing(true, true);
  @include inner-space("l", "t");
  @include inner-space("xl", "b");
  grid-gap: $space-m;

  @include breakpoint("small") {
    grid-gap: $space-xxl;
    @include inner-space("xxl", "t");
    @include inner-space("xxxl", "b");
  }
}
.event__card {
  @include container-spacing(true, true);
  margin-top: calc(-1 * var(--space-xxxl));
  z-index: 1;
  position: relative;
}
.event__cover {
  border-radius: $border-radius-l;
  overflow: hidden;
}

.event__external {
  @include inner-space("m", "y");
  @include inner-space("l", "x");
  border-radius: $border-radius-m;
  background: $color-default;
  color: $color-default-inverse;
  @include text-body-black;
  text-align: center;
}

.event__description--short {
  display: inline-block;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.map__wrapper {
  border-radius: $border-radius-l;
  overflow: hidden;
}

.map__address {
  @include inner-space("m", "b");
}

.link {
  text-decoration: none !important;
  border-bottom: 0;
}
.event__ad {
  position: fixed;
  right: $space-m;
  bottom: $space-xl;

  @include breakpoint("small", "max") {
    display: none;
  }
}
</style>
<style lang="scss">
.event__description {
  @include rte-render;
  color: $color-text;

  [class^="ql-align"] {
    text-align: left !important;
  }

  * {
    color: $color-text !important;
  }
}
</style>
